import type { AssetColors } from '../../palette.js';
import { lotusLeaf, naturePool } from './nature-water-shapes.js';

const WILLOWS = {
  arch: {
    trunk:
      'M-3,.2 Q.2,-1.7 -.7,-5.5 L-2.6,-8.1 -1.8,-8.6 .3,-6.5 1.5,-9.4 2.1,-9.1 1.1,-5.8 Q2.1,-2.5 -.1,-.3 L.8,.3 -1,.5Z',
    crown:
      'M-4.9,-2.9 Q-6.6,-7.5 -3.6,-9.4 Q-3.7,-12.1 -1.4,-12.2 Q.6,-14 2.2,-12.5 Q5,-12.4 5.4,-9.4 Q6.4,-7.6 5.6,-3.4 L4.7,-4.9 4.2,-2.5 Q2.8,-4.2 3.2,-7.5 L2.1,-5.1 1.6,-7.8 Q.2,-6.9 -1.1,-8.2 L-2,-4.8 -2.7,-6.3 -3.5,-2.5 -4.2,-4Z',
    light:
      'M-4.9,-3.6 Q-5.8,-7.6 -3.1,-9.4 Q-3.3,-11.8 -1.2,-11.9 Q.8,-13.6 2.2,-12.5 Q3.7,-12.4 4.2,-11.1 Q1.6,-11.5 .2,-10.3 Q-1.9,-10.7 -2.8,-8.1 L-3.9,-4.8 -3.8,-7.9Z',
    veins:
      'M-2.2,-9.8 Q-3.8,-7.2 -3.5,-4.2 M1,-10.7 Q2.1,-9.1 1.9,-7.4 M3.7,-10.2 Q5.1,-8.8 4.7,-5.2',
  },
  upright: {
    trunk:
      'M-1.8,.3 -.5,-1.2 -.8,-4.4 -2.1,-6.4 -1.6,-6.8 .1,-5 .6,-8.5 1.3,-8.4 .9,-4.8 2.8,-6.1 3,-5.6 .7,-3.6 .7,-.8 1.8,.4Z',
    crown:
      'M-4,-1.9 Q-5.1,-6 -3,-8.3 Q-3.2,-10.7 -1,-11.1 Q.7,-12.5 2.3,-10.7 Q4.1,-10.2 4,-8.1 Q5.2,-6 4.1,-1.9 L3.4,-3.6 2.9,-2.2 Q1.7,-4.1 2,-6.9 L1.1,-5.3 .5,-7.3 -.6,-6.6 -1.5,-3 -2.1,-4.5 -3,-1.5 -3.2,-3.4Z',
    light:
      'M-3.8,-3.2 Q-4.3,-6.6 -2.4,-8.4 Q-2.9,-10.7 -.8,-10.8 Q.6,-12 2.3,-10.7 L1.9,-9.5 Q-.1,-10.6 -1.1,-8.2 L-2.3,-4.2 -2.2,-7Z',
    veins: 'M-.9,-9.7 Q-2.1,-7.1 -2,-5 M1.6,-9.4 Q3.4,-7.1 3.4,-4',
  },
  swept: {
    trunk:
      'M-3.8,.3 Q-.6,-1.5 -.1,-4.7 L-1.8,-7.4 -1.1,-7.7 .8,-6 2,-8 2.6,-7.6 1.4,-5.3 3.3,-6.7 3.7,-6.1 1.2,-4.2 Q.8,-1.7 -.5,-.3 L.5,.3 -1.4,.6Z',
    crown:
      'M-4.9,-3.3 Q-5.9,-7.6 -3.2,-8.9 Q-2.8,-11.1 -.7,-11.1 Q1.2,-12.8 3,-11.2 Q5.9,-11.4 6.4,-8.8 Q7.8,-6.6 6.5,-3.1 L5.6,-4.7 5,-2.6 Q3.8,-4.4 4,-7.2 L2.9,-5.1 2.1,-7.6 Q.6,-6.5 -.8,-7.1 L-1.9,-3.9 -2.5,-5.2 -3.7,-2.8 -3.7,-4.5Z',
    light:
      'M-4.7,-4.1 Q-5.2,-7.7 -2.8,-8.9 Q-2.4,-10.9 -.5,-10.8 Q1.5,-12.4 3,-11.2 Q4.2,-11.2 4.8,-10.3 Q1.8,-10.7 .7,-9.1 Q-2,-9.6 -3,-6.8 L-3.8,-4.1 -3.5,-6.6Z',
    veins:
      'M-1.8,-9.1 Q-3.6,-7.6 -3.5,-4.8 M1.9,-9.7 Q3.3,-8.1 2.8,-6.7 M4.7,-9.8 Q6.2,-8.1 5.8,-5',
  },
} as const;
const PONDS = [
  [[-1.7, -0.8, 0.85, 'arch']],
  [
    [3.5, -1.9, 0.68, 'upright'],
    [-3.6, 0, 0.7, 'upright'],
  ],
  [[-1.8, -0.8, 1, 'swept']],
] as const;

export function svgWillowPond(x: number, y: number, c: AssetColors, v: number): string {
  const pond = PONDS[v] ?? PONDS[0];
  const trees = pond
    .map(([dx, dy, size, form]) => {
      const tree = WILLOWS[form];
      return (
        `<g transform="translate(${dx},${dy}) scale(${size})">` +
        `<path d="${tree.trunk}" fill="${c.trunk}"/>` +
        `<path d="${tree.crown}" fill="${c.bushDark}"/>` +
        `<path d="${tree.light}" fill="${c.willow}"/>` +
        `<path d="${tree.veins}" stroke="${c.leafLight}" stroke-width=".3" fill="none" stroke-linecap="round"/></g>`
      );
    })
    .join('');
  return (
    `<g transform="translate(${x},${y})">${naturePool(c, v)}` +
    lotusLeaf(c, 4.5, 0.2, 0.7) +
    lotusLeaf(c, 1.5, 1, 0.55) +
    trees +
    `<path d="M-5.6,.2 -4.8,-.7 -3.7,-.6 -3,.3 -4.6,.7Z" fill="${c.rock}"/>` +
    `<path d="M-5.6,.2 -4.8,-.7 -3.7,-.6 -4.2,.1Z" fill="${c.moss}"/></g>`
  );
}
